import { useState } from 'react';
import { SearchableSelect } from '@shared/components';
import { useDealersQuery } from '../hooks/useDealersQuery';
import type { Dealer } from '../types/dealers.types';

// This picker only ever offers the two partner ends of a franchise link: an
// 'oem' (on the OEMs tab) or a 'franchise_dealer' (on the Franchises tab).
// Narrowing off the full DealerType union stops invalid types (e.g.
// 'dealer_group') from compiling.
type FranchisePartnerType = 'oem' | 'franchise_dealer';

interface FranchisePartnerSelectProps {
    partnerType: FranchisePartnerType;
    value: Dealer | null;
    onChange: (dealer: Dealer | null) => void;
    excludeIds?: string[];
    disabled?: boolean;
    minChars?: number;
}

/**
 * Franchise-partner picker for the OEMs / Franchises tabs. Unlike
 * AllowedParentSelect (driven by the ownership-parentage matrix), this is
 * driven by a fixed partner type: 'oem' on the OEMs tab (this page is a
 * franchise_dealer picking an oem), 'franchise_dealer' on the Franchises tab
 * (this page is an oem picking a franchise_dealer). excludeIds hides
 * already-linked partners so the same pair cannot be picked twice.
 */
export function FranchisePartnerSelect({
    partnerType,
    value,
    onChange,
    excludeIds = [],
    disabled = false,
    minChars = 2,
}: FranchisePartnerSelectProps) {
    const [term, setTerm] = useState('');
    const canQuery = term.length >= minChars;

    const { data, isFetching } = useDealersQuery(
        // Live = any non-retired dealer (active family + pending); a retired
        // (inactive) dealer cannot be a franchise partner.
        { type: partnerType, is_live: true, search: term || undefined, per_page: 20 },
        { enabled: canQuery },
    );

    return (
        <SearchableSelect<Dealer>
            options={canQuery ? (data?.data ?? []) : value ? [value] : []}
            value={value}
            onChange={onChange}
            getOptionValue={(d) => d.id}
            getOptionLabel={(d) => d.name}
            onSearchChange={setTerm}
            loading={isFetching}
            excludeValues={excludeIds}
            clearable
            disabled={disabled}
            placeholder="Search dealers by name, code, city, or postcode..."
            emptyLabel={canQuery ? 'No dealers match.' : `Type at least ${minChars} characters to search.`}
            renderOption={(d) => (
                <div className="min-w-0 text-left w-full">
                    <div className="font-semibold text-sm">{d.name}</div>
                    <div className="text-xs text-mp-muted flex gap-2">
                        <span className="font-mono">{d.dealer_code}</span>
                        {d.city && <span>&middot; {d.city}</span>}
                    </div>
                </div>
            )}
            id="franchise_partner_id"
        />
    );
}
